import {
  type ButtonsObject,
  type ElementTypes,
  type InfluenceRelationNotation,
  standardShapeOptions,
} from '@bizzdesign/modeler/models';
import { getArchimateIcon } from '@bizzdesign/modeler/utils-toolbar';
import { type MenuItem } from 'primeng/api';

// Influence relation notation
export const influenceNotationIcons: {
  [key in InfluenceRelationNotation]?: string;
} = {
  InfluenceRelationNotationStronglyPositive: getArchimateIcon(
    'influence-strongly-positive',
  ),
  InfluenceRelationNotationPositive: getArchimateIcon('influence-positive'),
  InfluenceRelationNotationUnspecified: getArchimateIcon('influence-0'),
  InfluenceRelationNotationNegative: getArchimateIcon('influence-negative'),
  InfluenceRelationNotationStronglyNegative: getArchimateIcon(
    'influence-strongly-negative',
  ),
};

export const influenceNotationOptions: MenuItem[] = [
  {
    icon: influenceNotationIcons.InfluenceRelationNotationStronglyPositive,
    id: 'InfluenceRelationNotationStronglyPositive',
    label: 'Strongly positive',
  },
  {
    icon: influenceNotationIcons.InfluenceRelationNotationPositive,
    id: 'InfluenceRelationNotationPositive',
    label: 'Positive',
  },
  {
    icon: influenceNotationIcons.InfluenceRelationNotationUnspecified,
    id: 'InfluenceRelationNotationUnspecified',
    label: 'Unspecified',
  },
  {
    icon: influenceNotationIcons.InfluenceRelationNotationNegative,
    id: 'InfluenceRelationNotationNegative',
    label: 'Negative',
  },
  {
    icon: influenceNotationIcons.InfluenceRelationNotationStronglyNegative,
    id: 'InfluenceRelationNotationStronglyNegative',
    label: 'Strongly negative',
  },
];

// shapes
export const toolbarShapeOptions: MenuItem[] = standardShapeOptions;

// buttons that should be in every toolbar
export const baseToolbarButtons: ButtonsObject[] = [
  {
    key: 'createText',
    disabled: false,
    showSeparator: false,
    eventKey: 'createText',
  },
  {
    key: 'createSticky',
    disabled: false,
    showSeparator: false,
    eventKey: 'createSticky',
  },
  {
    key: 'createShape',
    disabled: false,
    showSeparator: true,
    eventKey: 'createShape',
  },
  {
    key: 'createFormalShape',
    disabled: false,
    showSeparator: true,
    eventKey: 'createFormalShape',
  },
];

export const graphActionsToolbarButtons: ButtonsObject[] = [
  ...baseToolbarButtons,
  {
    key: 'reuseEntityFromRepo',
    disabled: false,
    showSeparator: true,
    eventKey: 'reuseEntityFromRepo',
  },
  {
    key: 'attributePicker',
    disabled: false,
    showSeparator: false,
    eventKey: 'attributePicker',
  },
  {
    key: 'modelingAssistance',
    disabled: false,
    showSeparator: false,
    eventKey: 'modelingAssistance',
  },
];

export const groupMenuButton: ButtonsObject = {
  key: 'group',
  disabled: false,
  showSeparator: true,
  eventKey: 'group',
};

export const quickCreateToolbarButtons: ButtonsObject[] = [
  {
    key: 'createText',
    disabled: false,
    showSeparator: false,
    eventKey: 'createText',
  },
  {
    key: 'createSticky',
    disabled: false,
    showSeparator: true,
    eventKey: 'createSticky',
  },
  {
    key: 'createShape',
    disabled: false,
    showSeparator: false,
    eventKey: 'createShape',
  },
  {
    key: 'createFormalShape',
    disabled: false,
    showSeparator: false,
    eventKey: 'createFormalShape',
  },
];

export type ToolbarKey =
  | 'graphActionToolbarFrame'
  | 'graphActionToolbarView'
  | 'quickCreate'
  | 'contextMenu';

export const graphActionsToolbarViewModeButtons: ButtonsObject[] = [
  {
    key: 'addViewToFrame',
    disabled: false,
    showSeparator: false,
    eventKey: 'addViewToFrame',
  },
];

export const menuKeyMenuButtonsMap: Record<ToolbarKey, ButtonsObject[]> = {
  contextMenu: [],
  graphActionToolbarFrame: graphActionsToolbarButtons,
  graphActionToolbarView: graphActionsToolbarViewModeButtons,
  quickCreate: quickCreateToolbarButtons,
};

export const textMenuButtons: ButtonsObject[] = [
  {
    key: 'fontCustomize',
    disabled: false,
    showSeparator: true,
    eventKey: 'customize',
  },
  {
    key: 'moreMenu',
    disabled: false,
    showSeparator: false,
    eventKey: 'moreMenu',
  },
];

export const shapeTypeMenuButtons: ButtonsObject[] = [
  {
    key: 'changeShape',
    disabled: false,
    showSeparator: true,
    eventKey: 'changeShape',
  },
  {
    key: 'changeFillColor',
    disabled: false,
    showSeparator: true,
    eventKey: 'customize',
  },
  {
    key: 'fontCustomize',
    disabled: false,
    showSeparator: true,
    eventKey: 'customize',
  },
  {
    key: 'moreMenu',
    disabled: false,
    showSeparator: false,
    eventKey: 'moreMenu',
  },
];

export const groupTypeMenuButtons: ButtonsObject[] = [
  {
    key: 'changeFillColor',
    disabled: false,
    showSeparator: true,
    eventKey: 'customize',
  },
  {
    key: 'fontCustomize',
    disabled: false,
    showSeparator: true,
    eventKey: 'customize',
  },
  {
    key: 'group',
    disabled: false,
    showSeparator: true,
    eventKey: 'ungroup',
  },
  {
    key: 'moreMenu',
    disabled: false,
    showSeparator: false,
    eventKey: 'moreMenu',
  },
];

// Sticky notes reuse the generic shape toolbar plus the author toggle, which sits between the
// font group and the more menu (NGH-8401).
export const stickyTypeMenuButtons: ButtonsObject[] = [
  {
    key: 'changeShape',
    disabled: false,
    showSeparator: true,
    eventKey: 'changeShape',
  },
  {
    key: 'changeFillColor',
    disabled: false,
    showSeparator: true,
    eventKey: 'customize',
  },
  {
    key: 'fontCustomize',
    disabled: false,
    showSeparator: true,
    eventKey: 'customize',
  },
  {
    key: 'toggleStickyAuthor',
    disabled: false,
    showSeparator: true,
    eventKey: 'toggleStickyAuthor',
  },
  {
    key: 'moreMenu',
    disabled: false,
    showSeparator: false,
    eventKey: 'moreMenu',
  },
];

export const archimateTypeMenuButtons: ButtonsObject[] = [
  {
    key: 'changeShape',
    disabled: false,
    showSeparator: true,
    eventKey: 'changeShape',
  },
  {
    key: 'contentDetailsPanel',
    disabled: false,
    showSeparator: true,
    eventKey: 'contentDetailsPanel',
  },
  {
    key: 'changeFillColor',
    disabled: false,
    showSeparator: true,
    eventKey: 'customize',
  },
  {
    key: 'fontCustomize',
    disabled: false,
    showSeparator: true,
    eventKey: 'customize',
  },
  {
    key: 'moreMenu',
    disabled: false,
    showSeparator: false,
    eventKey: 'moreMenu',
  },
];

export const fromRepoTypeMenuButtons: ButtonsObject[] = [
  {
    key: 'changeShape',
    disabled: false,
    showSeparator: true,
    eventKey: 'changeShape',
  },
  {
    key: 'contentDetailsPanel',
    disabled: false,
    showSeparator: false,
    eventKey: 'contentDetailsPanel',
  },
  {
    key: 'loadAllNeighbors',
    disabled: false,
    showSeparator: true,
    eventKey: 'loadAllNeighbors',
  },
  {
    key: 'changeFillColor',
    disabled: false,
    showSeparator: true,
    eventKey: 'customize',
  },
  {
    key: 'fontCustomize',
    disabled: false,
    showSeparator: true,
    eventKey: 'customize',
  },
  {
    key: 'moreMenu',
    disabled: false,
    showSeparator: false,
    eventKey: 'moreMenu',
  },
];

export const deletedFromRepoTypeMenuButtons: ButtonsObject[] = [
  {
    key: 'changeShape',
    disabled: false,
    showSeparator: true,
    eventKey: 'changeShape',
  },
  {
    key: 'contentDetailsPanel',
    disabled: false,
    showSeparator: true,
    eventKey: 'contentDetailsPanel',
  },
  {
    key: 'moreMenu',
    disabled: false,
    showSeparator: false,
    eventKey: 'moreMenu',
  },
];

export const lineTypeMenuButtons: ButtonsObject[] = [
  {
    key: 'changeRelation',
    disabled: false,
    showSeparator: true,
    eventKey: 'changeShape',
  },
  {
    key: 'fontCustomize',
    disabled: false,
    showSeparator: true,
    eventKey: 'customize',
  },
  {
    key: 'relationAnchor',
    disabled: false,
    showSeparator: true,
    eventKey: 'relationAnchor',
  },
  {
    key: 'moreMenu',
    disabled: false,
    showSeparator: false,
    eventKey: 'moreMenu',
  },
];

export const lineTypeMenuWithInfluenceButtons: ButtonsObject[] = [
  {
    key: 'changeRelation',
    disabled: false,
    showSeparator: false,
    eventKey: 'changeShape',
  },
  {
    key: 'influenceNotation',
    disabled: false,
    showSeparator: true,
    eventKey: 'influenceNotation',
  },
  {
    key: 'fontCustomize',
    disabled: false,
    showSeparator: true,
    eventKey: 'customize',
  },
  {
    key: 'relationAnchor',
    disabled: false,
    showSeparator: false,
    eventKey: 'relationAnchor',
  },
  {
    key: 'moreMenu',
    disabled: false,
    showSeparator: false,
    eventKey: 'moreMenu',
  },
];

const elementTypeMenuButtonsMap: Record<ElementTypes, ButtonsObject[]> = {
  shape: shapeTypeMenuButtons,
  archiMate: archimateTypeMenuButtons,
  fromRepo: fromRepoTypeMenuButtons,
  line: lineTypeMenuButtons,
  relation: lineTypeMenuButtons,
  formalObject: archimateTypeMenuButtons,
  text: textMenuButtons,
  group: groupTypeMenuButtons,
};

export function getMenuButtonsByElementType(
  elementType: ElementTypes,
  isDeletedFromRepo = false,
): ButtonsObject[] {
  if (elementType === 'fromRepo' && isDeletedFromRepo) {
    return deletedFromRepoTypeMenuButtons;
  }

  return elementTypeMenuButtonsMap[elementType];
}
